import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Box, Typography,
  FormControl, Select, MenuItem, Stack, Chip, IconButton, InputAdornment,
  ToggleButton, ToggleButtonGroup,
  Snackbar, Alert
} from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import SimCardOutlinedIcon from "@mui/icons-material/SimCardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Autocomplete from "@mui/material/Autocomplete";
import { getChipsInactivos } from "../../services/chips";
import { getPlataformasActivas } from "../../services/plataformas";
import { getModemSensoresByNumSim, normalizeSensores } from "../../services/modems";

const SENSOR_LABELS = {
  buzer: "Buzzer",
  ibutton: "iButton",
  corte_motor: "Corte motor",
  temperatura: "Temperatura",
  acelerometro: "Acelerómetro",
  boton_panico: "Botón de pánico",
  sensor_puerta: "Sensor de puerta",
};

// === helpers ===
const norm = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
const isMaybeIp = (s) => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(String(s || "").trim());
const getPlatformField = (p, keys) => {
  for (const k of keys) if (p?.[k] != null) return p[k];
  return undefined;
};
const findPlatformByHint = (list, hint) => {
  const h = (hint ?? "").toString().trim();
  if (!h) return null;
  const H = norm(h);
  const ipKeys = ["ip", "direccion_ip", "host"];
  const nameKeys = ["nombre", "name", "label", "alias"];
  if (isMaybeIp(h)) {
    return list.find((p) => norm(getPlatformField(p, ipKeys)) === H) || null;
  }
  let found = list.find((p) => norm(getPlatformField(p, nameKeys)) === H) || null;
  if (found) return found;
  found = list.find((p) => {
    const n = norm(getPlatformField(p, nameKeys));
    return n && (n.includes(H) || H.includes(n));
  }) || null;
  if (!found) {
    found = list.find((p) => {
      const ipn = norm(getPlatformField(p, ipKeys));
      return ipn && (ipn.includes(H) || H.includes(ipn));
    }) || null;
  }
  return found;
};

// genera un objeto de sensores con todos en false
const makeAllFalseSensores = () =>
  Object.fromEntries(Object.keys(SENSOR_LABELS).map((k) => [k, false]));

export default function AssignDialog({
  open,
  onClose,
  modemTarget,            // { num_sim, sucursal, estado, chip_relacionado?, ip?, equipo?: { ip? }, equipo_ip? }
  assigningChip = false,
  onAssign,
  // Dentro de AsignarEquipoDialog (paso 2 de 2): sin Dialog propio y con "Atrás" a la búsqueda.
  embedded = false,
  onBack,
}) {
  // ---- Chip (Autocomplete) ----
  const [chipOptions, setChipOptions] = useState([]);
  // Uno o varios chips (módems de doble SIM). Con un módem Programado, el chip relacionado
  // va fijo en la lista (no se puede quitar) y se pueden sumar otros.
  const [chipValues, setChipValues] = useState([]);
  const [chipInput, setChipInput] = useState("");
  const [chipLoading, setChipLoading] = useState(false);
  // Buscador abierto para sumar otro chip ("+ Agregar otro chip").
  const [agregandoChip, setAgregandoChip] = useState(false);

  // ---- Origen (Select) ----
  const [plataformas, setPlataformas] = useState([]);
  const [platLoading, setPlatLoading] = useState(false);
  const [origenId, setOrigenId] = useState(""); // string siempre
  const [autoOrigenByHint, setAutoOrigenByHint] = useState(false);
  const [autoHintText, setAutoHintText] = useState("");

  // ---- Bandera ----
  const [bandera, setBandera] = useState(false);
  const [sensor, setSensor] = useState(false);

  // ---- Sensores ----
  const [sensores, setSensores] = useState({});
  const [sensoresLoading, setSensoresLoading] = useState(false);
  const [sensoresBackup, setSensoresBackup] = useState(null); // para restaurar al salir de Bandera

  // ---- Validación / Mensajes ----
  const [chipError, setChipError] = useState("");
  const [origenError, setOrigenError] = useState("");
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  // ====== Locks por estado ======
  const relatedNumero = useMemo(() => {
    const rel = modemTarget?.chip_relacionado;
    if (rel && typeof rel === "object") return rel?.numero ?? null;
    return rel ?? null;
  }, [modemTarget]);

  const lockChip = useMemo(() => {
    const estado = String(modemTarget?.estado || "").toLowerCase();
    return estado === "programado" && !!relatedNumero;
  }, [modemTarget?.estado, relatedNumero]);

  const lockBandera = useMemo(() => {
    const estado = String(modemTarget?.estado || "").toLowerCase();
    return estado === "programado";
  }, [modemTarget?.estado]);

  const bypassMode = bandera || sensor;

  // Hint (IP o nombre, p.ej. "Wialon") para autoseleccionar origen
  const modemHint = useMemo(() => {
    const ip =
      modemTarget?.ip ??
      modemTarget?.equipo?.ip ??
      modemTarget?.equipo_ip ??
      modemTarget?.plataforma ??
      modemTarget?.platform ??
      null;
    return (ip ? String(ip) : "").trim();
  }, [modemTarget]);

  const plataformasIdSet = useMemo(
    () => new Set(plataformas.map((p) => String(p.id))),
    [plataformas]
  );

  const selectValue = useMemo(() => {
    if (bypassMode) return "";
    const v = String(origenId || "");
    return plataformasIdSet.has(v) ? v : "";
  }, [bypassMode, origenId, plataformasIdSet]);

  const canAssign = useMemo(() => {
    if (assigningChip) return false;
    if (bypassMode) return true;
    return chipValues.some((c) => !!c?.numero);
  }, [assigningChip, bypassMode, chipValues]);

  const relatedPreset = useMemo(() => (lockChip ? {
    numero: relatedNumero,
    emai: typeof modemTarget?.chip_relacionado === "object" ? modemTarget.chip_relacionado.emai : undefined,
    ciudad: typeof modemTarget?.chip_relacionado === "object" ? modemTarget.chip_relacionado.ciudad : undefined,
    estado: "Relacionado",
  } : null), [lockChip, relatedNumero, modemTarget]);

  // ---------- Inicialización al abrir ----------
  useEffect(() => {
    if (!open) return;

    setBandera(false);
    setSensor(false);
    setChipValues([]);
    setChipInput("");
    setAgregandoChip(false);
    setOrigenId("");
    setChipError("");
    setOrigenError("");
    setAutoOrigenByHint(false);
    setAutoHintText("");
    setSensoresBackup(null);

    // Chip preset si Programado con chip_relacionado
    if (lockChip) {
      setChipValues([relatedPreset]);
      setChipInput("");
      setChipOptions([]);
    } else {
      const suc = modemTarget?.sucursal || "";
      const estado = modemTarget?.estado || "";
      if (estado === "Inactivo") {
        setChipLoading(true);
        getChipsInactivos({ ciudad: suc, buscar: "" })
          .then((resp) => setChipOptions(resp?.data || []))
          .catch(() => setChipOptions([]))
          .finally(() => setChipLoading(false));
      } else {
        setChipOptions([]);
      }
    }

    // Plataformas + autoselección por IP/nombre (sin default a primera)
    setPlatLoading(true);
    getPlataformasActivas()
      .then((list) => {
        const activos = list || [];
        setPlataformas(activos);
        if (modemHint) {
          const found = findPlatformByHint(activos, modemHint);
          if (found?.id != null) {
            setOrigenId(String(found.id));
            setAutoOrigenByHint(true);
            setAutoHintText(modemHint);
          }
        }
      })
      .catch(() => setPlataformas([]))
      .finally(() => setPlatLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modemTarget?.sucursal, modemTarget?.estado, lockChip, relatedNumero, modemHint]);

  // ---------- Sensores ----------
  useEffect(() => {
    if (!open) return;
    const numSim = modemTarget?.num_sim ? String(modemTarget.num_sim).trim() : "";
    if (!numSim) { setSensores({}); return; }

    let alive = true;
    setSensoresLoading(true);
    getModemSensoresByNumSim(numSim)
      .then(({ data }) => {
        if (!alive) return;
        const n = normalizeSensores(data);
        // Si Bandera está activa, mantener todo en false (no sobrescribir)
        setSensores(bypassMode ? makeAllFalseSensores() : (n.sensores || {}));
      })
      .catch(() => { if (alive) setSensores(bypassMode ? makeAllFalseSensores() : {}); })
      .finally(() => { if (alive) setSensoresLoading(false); });

    return () => { alive = false; };
  }, [open, modemTarget?.num_sim, bypassMode]);

  // ---------- Debounce chips ----------
  useEffect(() => {
    if (!open || bypassMode) return;
    const id = setTimeout(() => {
      const suc = modemTarget?.sucursal || "";
      setChipLoading(true);
      getChipsInactivos({ ciudad: suc, buscar: chipInput.trim() })
        .then((resp) => setChipOptions(resp?.data || []))
        .catch(() => setChipOptions([]))
        .finally(() => setChipLoading(false));
    }, 400);
    return () => clearTimeout(id);
  }, [open, bypassMode, chipInput, modemTarget?.sucursal]);

  // ---------- Handlers ----------
  const handleToggleBandera = (checked) => {
    if (lockBandera) return;
    setBandera(checked);
    if (checked) setSensor(false);
    if (checked) {
      // Respaldar sensores actuales y forzar todos a false
      setSensoresBackup((prev) => prev ?? sensores);
      setSensores(makeAllFalseSensores());

      // Limpiar y bloquear Chip + Origen (comportamiento original)
      setChipValues([]);
      setChipInput("");
      setOrigenId("");
      setChipError("");
      setOrigenError("");
      setAutoOrigenByHint(false);
      setAutoHintText("");
    } else {
      // Restaurar sensores previos si había respaldo; si no, quedan como estaban
      if (sensoresBackup) {
        setSensores(sensoresBackup);
        setSensoresBackup(null);
      }
      // Si hay chip relacionado, restaurar preset
      if (lockChip) {
        setChipValues([relatedPreset]);
        setChipInput("");
      }
      // Reintentar autoselección por hint si estaba vacío
      if (!origenId && modemHint && plataformas.length) {
        const found = findPlatformByHint(plataformas, modemHint);
        if (found?.id != null) {
          setOrigenId(String(found.id));
          setAutoOrigenByHint(true);
          setAutoHintText(modemHint);
        }
      }
    }
  };

  const handleToggleSensor = (checked) => {
    if (lockBandera) return;
    setSensor(checked);
    if (checked) {
      setBandera(false);
      setSensoresBackup((prev) => prev ?? sensores);
      setSensores(makeAllFalseSensores());
      setChipValues([]);
      setChipInput("");
      setOrigenId("");
      setChipError("");
      setOrigenError("");
      setAutoOrigenByHint(false);
      setAutoHintText("");
    } else {
      if (sensoresBackup) {
        setSensores(sensoresBackup);
        setSensoresBackup(null);
      }
      if (lockChip) {
        setChipValues([relatedPreset]);
        setChipInput("");
      }
      if (!origenId && modemHint && plataformas.length) {
        const found = findPlatformByHint(plataformas, modemHint);
        if (found?.id != null) {
          setOrigenId(String(found.id));
          setAutoOrigenByHint(true);
          setAutoHintText(modemHint);
        }
      }
    }
  };

  const validateFields = () => {
    // ✅ Regla: Bypass si la bandera está activa
    if (bypassMode) return true; 
  
    let ok = true;
  
    // 1. Validación de los chips (aceptan String): al menos uno, cada uno de 3 a 20 caracteres
    const numeros = chipValues.map((c) => String(c?.numero || "").trim()).filter(Boolean);
    const invalido = numeros.find((n) => n.length < 3 || n.length > 20);

    if (!numeros.length) {
      setChipError("El chip es requerido.");
      ok = false;
    } else if (invalido) {
      setChipError(`El chip ${invalido} debe tener entre 3 y 20 caracteres.`);
      ok = false;
    } else {
      setChipError("");
    }
  
    // 2. Validación del Origen
    if (selectValue) {
      const exists = plataformasIdSet.has(String(selectValue));
      if (!exists) {
        setOrigenError("Origen inválido.");
        ok = false;
      } else {
        setOrigenError("");
      }
    } else {
      setOrigenError("");
    }
  
    return ok;
  };

  const handleAssign = async () => {
    if (!validateFields()) {
      setSnack({ open: true, severity: "warning", msg: "Revisa los campos requeridos." });
      return;
    }
    if (!canAssign) return;

    // FIX: solo llama onAssign — el padre maneja toast, cierre y error.
    // No llamamos onClose aquí porque handleAssignChip ya llama handleCloseChip.
    if (bandera) {
      await onAssign?.({
        bandera: true,
        sensores: makeAllFalseSensores(),
      });
    } else if (sensor) {
      await onAssign?.({
        sensor: true,
        sensores: makeAllFalseSensores(),
      });
    } else {
      const plataforma = plataformas.find((p) => String(p.id) === String(selectValue));
      await onAssign?.({
        numeros:       chipValues.map((c) => String(c.numero).trim()).filter(Boolean),
        origen_id:     selectValue ? String(selectValue) : null,
        origen_nombre: plataforma?.nombre || null,
        bandera:       false,
        sensores:      { ...sensores },
      });
    }
  };

  // ---------- UI ----------
  // Mismo esquema que "Configurar equipo" en la app Android: tarjeta del equipo elegido, modo
  // (Con chip / Bandera / Sensor), chips como tarjetas con "+ Agregar otro chip", origen y
  // sensores como fichas que se prenden y apagan; el botón de asignar va fijo abajo.
  const modo = bandera ? "bandera" : sensor ? "sensor" : "chip";
  const handleModo = (_, nuevo) => {
    if (!nuevo || nuevo === modo) return;
    if (nuevo === "bandera") handleToggleBandera(true);
    else if (nuevo === "sensor") handleToggleSensor(true);
    else if (bandera) handleToggleBandera(false);
    else handleToggleSensor(false);
  };

  const elegidos = new Set(chipValues.map((c) => String(c?.numero)));
  const opcionesChip = chipOptions.filter((o) => !elegidos.has(String(o?.numero)));
  const mostrarBuscadorChip = chipValues.length === 0 || agregandoChip;
  const esFijo = (c) => !!relatedPreset && String(c?.numero) === String(relatedPreset.numero);

  const agregarChip = (chip) => {
    if (!chip?.numero) return;
    const numero = String(chip.numero).trim();
    if (numero.length < 3 || numero.length > 25) {
      setChipError("Longitud inválida (3–25 caracteres).");
      return;
    }
    setChipValues((prev) => (prev.some((c) => String(c.numero) === numero) ? prev : [...prev, chip]));
    setChipError("");
    setChipInput("");
    setAgregandoChip(false);
  };
  const quitarChip = (chip) => {
    if (esFijo(chip)) return;
    setChipValues((prev) => prev.filter((c) => String(c.numero) !== String(chip.numero)));
  };

  const textoModo = lockBandera
    ? "El equipo está Programado: solo se puede asignar con chip."
    : bandera
      ? "Se asigna como bandera, sin chip ni sensores."
      : sensor
        ? "Se asigna como sensor, sin chip ni sensores."
        : "Elige el chip de datos (o varios si el equipo es doble SIM) y marca los sensores que lleva.";

  const contenido = (
    <Stack spacing={2.5}>
      {/* Equipo elegido */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 1.5, p: 1.75, borderRadius: "10px", color: "#fff",
          background: `linear-gradient(135deg, ${AZUL_EQUIPOS}, ${AZUL_EQUIPOS_OSCURO})`,
        }}
      >
        <Box sx={{ width: 46, height: 46, borderRadius: "8px", bgcolor: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <MemoryIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Equipo elegido</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {modemTarget?.emai || modemTarget?.id || "—"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }} noWrap>
            {[modemTarget?.num_sim && modemTarget?.num_sim !== modemTarget?.emai ? `SIM ${modemTarget.num_sim}` : null, modemTarget?.modelo]
              .filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        {modemTarget?.estado && (
          <Box sx={{ px: 1.25, py: 0.5, borderRadius: 99, bgcolor: "rgba(255,255,255,0.2)", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
            {modemTarget.estado}
          </Box>
        )}
      </Box>

      {/* Modo */}
      <Box>
        <EtiquetaSeccion>Cómo se asigna</EtiquetaSeccion>
        <ToggleButtonGroup
          exclusive fullWidth size="small" color="primary"
          value={modo} onChange={handleModo} disabled={assigningChip}
        >
          <ToggleButton value="chip" sx={{ textTransform: "none", fontWeight: 600 }}>Con chip</ToggleButton>
          <ToggleButton value="bandera" disabled={lockBandera} sx={{ textTransform: "none", fontWeight: 600 }}>Bandera</ToggleButton>
          <ToggleButton value="sensor" disabled={lockBandera} sx={{ textTransform: "none", fontWeight: 600 }}>Sensor</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{textoModo}</Typography>
      </Box>

      {!bypassMode && (
        <>
          {/* Chips */}
          <Box>
            <EtiquetaSeccion>{chipValues.length > 1 ? `Chips de datos (${chipValues.length})` : "Chip de datos"}</EtiquetaSeccion>
            <Stack spacing={1}>
              {chipValues.map((c) => (
                <Box
                  key={c.numero}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, pl: 1.5, pr: 0.5, py: 1, borderRadius: "10px",
                    bgcolor: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.35)",
                  }}
                >
                  <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: VERDE, color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <SimCardOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{c.numero}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {esFijo(c)
                        ? "Chip relacionado al equipo (fijo)"
                        : [c.ciudad, c.estado].filter(Boolean).join(" · ") || "Chip elegido"}
                    </Typography>
                  </Box>
                  {esFijo(c) ? (
                    <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                  ) : (
                    <IconButton size="small" aria-label={`Quitar chip ${c.numero}`} onClick={() => quitarChip(c)} disabled={assigningChip}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}

              {mostrarBuscadorChip ? (
                <Box>
                  {chipValues.length > 0 && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Otro chip</Typography>
                      <Button size="small" onClick={() => { setAgregandoChip(false); setChipInput(""); setChipError(""); }}>Cancelar</Button>
                    </Stack>
                  )}
                  <Autocomplete
                    options={opcionesChip}
                    value={null}
                    onChange={(_, v) => agregarChip(v)}
                    inputValue={chipInput}
                    onInputChange={(_, v, reason) => { if (reason !== "reset") setChipInput(v); }}
                    loading={chipLoading}
                    autoHighlight
                    blurOnSelect
                    filterOptions={(x) => x}
                    getOptionLabel={(o) => o?.numero || ""}
                    isOptionEqualToValue={(o, v) => o?.numero === v?.numero}
                    noOptionsText={chipLoading ? "Buscando chips…" : chipInput ? "No hay chips inactivos que coincidan" : "Escribe el número o usa el lector"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoFocus={chipValues.length > 0}
                        placeholder="Número o EMAI del chip"
                        size="small"
                        error={!!chipError}
                        helperText={chipError || "Toca un chip de la lista para elegirlo"}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <InputAdornment position="start"><SimCardOutlinedIcon fontSize="small" /></InputAdornment>,
                          endAdornment: (
                            <>
                              {chipLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.numero}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <SimCardOutlinedIcon fontSize="small" sx={{ color: VERDE }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.numero}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[option.emai && option.emai !== option.numero ? option.emai : null, option.ciudad, option.estado].filter(Boolean).join(" · ")}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                  />
                </Box>
              ) : (
                <Box>
                  {chipError && <Typography variant="caption" color="error" display="block">{chipError}</Typography>}
                  <Button startIcon={<AddIcon />} onClick={() => setAgregandoChip(true)} disabled={assigningChip} sx={{ textTransform: "none" }}>
                    Agregar otro chip
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Origen */}
          <Box>
            <EtiquetaSeccion>Plataforma de origen</EtiquetaSeccion>
            <FormControl fullWidth size="small" error={!!origenError}>
              <Select
                value={selectValue}
                onChange={(e) => {
                  setOrigenId(String(e.target.value));
                  setAutoOrigenByHint(false);
                  setAutoHintText("");
                  setOrigenError("");
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {platLoading ? "Cargando..." : "Sin origen"}
                      </Typography>
                    );
                  }
                  const found = plataformas.find((p) => String(p.id) === String(selected));
                  return found?.nombre || String(selected);
                }}
              >
                <MenuItem value=""><em>Sin origen</em></MenuItem>
                {plataformas.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.nombre}{p?.ip ? ` · ${p.ip}` : ""}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color={origenError ? "error" : "text.secondary"} sx={{ mt: 0.5 }}>
                {origenError || (autoOrigenByHint && autoHintText ? `Elegida según el equipo: ${autoHintText}` : "Opcional")}
              </Typography>
            </FormControl>
          </Box>

          {/* Sensores */}
          <Box>
            <EtiquetaSeccion>Sensores instalados</EtiquetaSeccion>
            {sensoresLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Cargando sensores…</Typography>
              </Box>
            ) : (
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {Object.entries(SENSOR_LABELS).map(([k, label]) => {
                  const activo = !!sensores?.[k];
                  return (
                    <Chip
                      key={k}
                      label={label}
                      icon={activo ? <CheckIcon /> : undefined}
                      color={activo ? "primary" : "default"}
                      variant={activo ? "filled" : "outlined"}
                      onClick={() => setSensores((prev) => ({ ...prev, [k]: !prev?.[k] }))}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Stack>
  );

  const textoBoton = assigningChip
    ? "Asignando..."
    : bandera ? "Confirmar bandera" : sensor ? "Confirmar sensor" : "Asignar equipo";

  const pie = (
    <Box sx={{ width: "100%" }}>
      <Button
        fullWidth size="large" variant="contained" onClick={handleAssign} disabled={!canAssign}
        startIcon={assigningChip ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
        sx={{ py: 1.25, fontWeight: 700, textTransform: "none" }}
      >
        {textoBoton}
      </Button>
      {!canAssign && !assigningChip && (
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 0.75 }}>
          Elige el chip para poder asignar
        </Typography>
      )}
    </Box>
  );

  const snackbar = (
    <Snackbar
      open={snack.open}
      autoHideDuration={2600}
      onClose={() => setSnack(s => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        severity={snack.severity}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snack.msg}
      </Alert>
    </Snackbar>
  );

  // Dentro del diálogo de 2 pasos (AsignarEquipoDialog): solo cabecera, contenido y pie.
  if (embedded) {
    return (
      <>
        <CabeceraPaso titulo="Configurar equipo" subtitulo="Paso 2 de 2" onAtras={onBack} onCerrar={onClose} />
        <DialogContent dividers>{contenido}</DialogContent>
        <DialogActions sx={{ p: 2 }}>{pie}</DialogActions>
        {snackbar}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <CabeceraPaso titulo="Configurar equipo" onCerrar={onClose} />
      <DialogContent dividers>{contenido}</DialogContent>
      <DialogActions sx={{ p: 2 }}>{pie}</DialogActions>
      {snackbar}
    </Dialog>
  );
}

const AZUL_EQUIPOS = "#0284C7";
const AZUL_EQUIPOS_OSCURO = "#075985";
const VERDE = "#16A34A";

function EtiquetaSeccion({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", fontWeight: 700, lineHeight: 2, letterSpacing: 0.6 }}>
      {children}
    </Typography>
  );
}

/** Cabecera de un paso del diálogo: atrás (opcional), título, subtítulo y cerrar. */
export function CabeceraPaso({ titulo, subtitulo, onAtras, onCerrar }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
      {onAtras && (
        <IconButton onClick={onAtras} aria-label="Atrás">
          <ArrowBackIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{titulo}</Typography>
        {subtitulo && <Typography variant="caption" color="text.secondary">{subtitulo}</Typography>}
      </Box>
      <IconButton onClick={onCerrar} aria-label="Cerrar">
        <CloseIcon />
      </IconButton>
    </Box>
  );
}
